import {useCallback, useMemo} from 'react';
import {QueryKey, useQueryClient, UseQueryResult, useMutation, useQuery} from 'react-query';

import pick from 'lodash/pick';

import {PageOrder, Question, Revision, RevisionState, Survey, SurveySlug} from 'client/common/types';

import {
    createRevision,
    getRevision,
    publishSurvey,
    updateRevision
} from 'client/common/utils/api-client';

import {getActiveRevision, getCurrentRevision, getFlatPageOrder, getQuestionIdsNotBound} from 'client/common/utils/revision';

import {getSurveyKey, useSurvey} from 'client/common/hooks/survey';

import {useSurveySlug} from 'client/common/contexts/slug';

const REVISION_KEY = 'revision';

export function getRevisionKey(slug: SurveySlug, revisionIndex: number): QueryKey {
    return [REVISION_KEY, {slug, revisionIndex}];
}

export function useRevision(
    slug: SurveySlug,
    revisionId: Revision['id']
): UseQueryResult<Revision> {
    return useQuery(
        getRevisionKey(slug, revisionId),
        async () => {
            const {data} = await getRevision({
                slug,
                revisionIndex: revisionId,
                apiLocation: 'survey'
            });

            return data;
        },
        {
            enabled: revisionId > 0
        }
    );
}

export function useCurrentRevisionId(slug: SurveySlug): Revision['id'] {
    const survey = useSurvey(slug);

    // Если опрос не прогрузился, значит что-то пошло не так
    if (survey.status !== 'success') {
        return 0;
    }

    return getCurrentRevision(survey.data.revisions).id;
}

export function useActiveRevisionId(slug: SurveySlug): Revision['id'] | undefined {
    const survey = useSurvey(slug);

    if (survey.status !== 'success') {
        return undefined;
    }

    return getActiveRevision(survey.data.revisions)?.id;
}

export function useCurrentRevision(slug: SurveySlug): UseQueryResult<Revision> {
    const revisionId = useCurrentRevisionId(slug);

    return useRevision(slug, revisionId);
}

export function useActiveRevision(slug: SurveySlug): UseQueryResult<Revision | undefined> {
    const revisionId = useActiveRevisionId(slug);

    return useRevision(slug, revisionId ?? 0);
}

export function useCreateRevision(slug: SurveySlug) {
    const queryClient = useQueryClient();
    const {mutateAsync} = useMutation(createRevision, {
        onSuccess: res => {
            queryClient.setQueryData(getSurveyKey(slug), (old: Survey) => {
                const now = new Date().toISOString();

                const newRevision = {
                    ...pick(res.data, ['id', 'name', 'state']),
                    createdAt: now,
                    updatedAt: now
                };

                return {
                    ...old,
                    revisions: [newRevision].concat(old.revisions)
                };
            });

            queryClient.setQueryData(getRevisionKey(slug, res.data.id), res.data);
        }
    });

    return useCallback(
        async (revisionId?: Revision['id']) => {
            return mutateAsync({slug, revisionId});
        },
        [mutateAsync, slug]
    );
}

export function usePublishRevision() {
    const slug = useSurveySlug();
    const revisionId = useCurrentRevisionId(slug);

    const onRevisionCreate = useCreateRevision(slug);

    const revisionKey = getRevisionKey(slug, revisionId);
    const surveyKey = getSurveyKey(slug);

    const queryClient = useQueryClient();

    const {mutateAsync} = useMutation(publishSurvey, {
        onMutate: async () => {
            await queryClient.cancelQueries(revisionKey);

            const shapshot = queryClient.getQueryData(revisionKey);

            queryClient.setQueryData(revisionKey, (old: Revision) => ({
                ...old,
                state: RevisionState.ACTIVE
            }));

            return shapshot;
        },
        onError: (_error, _, shapshot) => {
            queryClient.setQueryData(revisionKey, shapshot);
        },
        onSettled: async () => {
            await onRevisionCreate(revisionId);

            await queryClient.refetchQueries(surveyKey);
        }
    });

    return useCallback(async () => {
        return mutateAsync({slug, revisionId});
    }, [mutateAsync, slug, revisionId]);
}

export function useUpdateRevision() {
    const slug = useSurveySlug();
    const revisionId = useCurrentRevisionId(slug);

    const key = getRevisionKey(slug, revisionId);

    const queryClient = useQueryClient();

    const {mutate} = useMutation(updateRevision, {
        onMutate: async variables => {
            await queryClient.cancelQueries(key);

            queryClient.setQueryData(key, (old: Revision) => ({
                ...old,
                ...variables.data
            }));
        },
        onError: async () => {
            await queryClient.refetchQueries(key);
        }
    });

    return useCallback(
        async (data: Partial<Revision>) => {
            mutate({
                slug,
                revisionId,
                data
            });
        },
        [mutate, slug, revisionId]
    );
}

export function useQuestions(pageOrder?: PageOrder): Question[] {
    const slug = useSurveySlug();
    const revision = useCurrentRevision(slug);

    return useMemo(() => {
        if (!revision.data) return [];

        const flatPageOrder = getFlatPageOrder(pageOrder || revision.data.pageOrder);

        return flatPageOrder.reduce<number[]>((acc, groupId) => {
            const {children} = revision.data.groups[groupId];
            const questionIds = children.filter(q => q.type === 'question').map(q => q.id);
            
            acc.push(...questionIds);
            
            return acc;
        }, []).map(qid => revision.data.questions[qid]);
    }, [pageOrder, revision.data]);
}

export function useCountQuestions(pageOrder?: PageOrder): number {
    const questions = useQuestions(pageOrder);

	return getQuestionIdsNotBound(questions).length;
}
